// Lib
import { isObject, capitalize } from 'lodash/fp';
import dateformat from 'dateformat';

// Utils
import { getStartOfDayDate, getStartOfYearDate, TIMES } from '../timeUtil';

/**
 * Map SupportedLanguage (CMS locale) to Intl locale string
 * Maps from 'en-au', 'es-es' to proper Intl locales like 'en-US', 'es-ES'
 * Also handles direct Intl locale strings like 'en-US', 'es-ES'
 */
export const mapLocalePreferenceToIntlLocale = (localePreference?: string): string => {
    if (!localePreference) return 'en-US';

    // Normalize to lowercase for comparison
    const normalizedLocale = localePreference.toLowerCase();

    // Handle the CMS locale format (en-au, es-es) and Intl formats (en-US, es-ES)
    if (normalizedLocale.startsWith('en')) {
        return 'en-US';
    }
    if (normalizedLocale.startsWith('es')) {
        return 'es-ES';
    }

    // Default fallback
    return 'en-US';
};

/**
 * Format a past date using native Intl.DateTimeFormat and Intl.RelativeTimeFormat
 * @param testTime - The timestamp to format
 * @param relativeTimestamp - The reference timestamp to compare against
 * @param locale - The locale for formatting
 * @param simpleFormat - Optional dateformat pattern for dates within the current year
 * @param longFormat - Optional dateformat pattern for dates in other years
 */
const formatPastDateIntl = (
    testTime: number,
    relativeTimestamp: number,
    locale: string,
    simpleFormat?: string,
    longFormat?: string,
): string => {
    const startOfDayDate = getStartOfDayDate(relativeTimestamp);
    const startOfDayMillis = startOfDayDate.getTime();

    // Today
    if (testTime >= startOfDayMillis) {
        // Use Intl.RelativeTimeFormat for "today" and capitalize to match original behavior
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        return capitalize(rtf.format(0, 'day'));
    }

    const startOfYesterdayMillis = startOfDayMillis - TIMES.DAY;

    // Yesterday
    if (testTime >= startOfYesterdayMillis) {
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        return capitalize(rtf.format(-1, 'day'));
    }

    // If custom formats are provided, use them
    if (simpleFormat || longFormat) {
        const startOfYearDate = getStartOfYearDate(relativeTimestamp);
        const format = testTime > startOfYearDate.valueOf() ? (simpleFormat || 'dddd, mmm d') : (longFormat || 'dddd, mmm d, yyyy');
        return dateformat(testTime, format);
    }

    // For other dates, format with weekday and date
    const date = new Date(testTime);
    const currentYear = new Date(relativeTimestamp).getFullYear();
    const dateYear = date.getFullYear();

    if (dateYear === currentYear) {
        // Same year: show weekday and date (e.g., "Monday, Feb 11" or "lunes, 11 feb" in Spanish)
        return new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
        }).format(date);
    } else {
        // Different year: include year (e.g., "Tuesday, Dec 4, 2018")
        return new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(date);
    }
};

/**
 * Format a future date using native Intl.DateTimeFormat and Intl.RelativeTimeFormat
 * @param testTime - The timestamp to format
 * @param relativeTimestamp - The reference timestamp to compare against
 * @param locale - The locale for formatting
 * @param simpleFormat - Optional dateformat pattern for dates within the current year
 * @param longFormat - Optional dateformat pattern for dates in other years
 */
const formatFutureDateIntl = (
    testTime: number,
    relativeTimestamp: number,
    locale: string,
    simpleFormat?: string,
    longFormat?: string,
): string => {
    const startOfDayDate = getStartOfDayDate(relativeTimestamp);
    const startOfDayMillis = startOfDayDate.getTime();
    const startOfTomorrowMillis = startOfDayMillis + TIMES.DAY;
    const startOfDayAfterTomorrowMillis = startOfTomorrowMillis + TIMES.DAY;

    // Today
    if (testTime < startOfTomorrowMillis) {
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        return capitalize(rtf.format(0, 'day'));
    }

    // Tomorrow
    if (testTime < startOfDayAfterTomorrowMillis) {
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        return capitalize(rtf.format(1, 'day'));
    }

    // If custom formats are provided, use them
    if (simpleFormat || longFormat) {
        const startOfNextYearDate = getStartOfYearDate(relativeTimestamp);
        startOfNextYearDate.setFullYear(startOfNextYearDate.getFullYear() + 1);
        const format = testTime < startOfNextYearDate.valueOf() ? (simpleFormat || 'dddd, mmm d') : (longFormat || 'dddd, mmm d, yyyy');
        return dateformat(testTime, format);
    }

    // For other dates, format with weekday and date
    const date = new Date(testTime);
    const currentYear = new Date(relativeTimestamp).getFullYear();
    const dateYear = date.getFullYear();

    if (dateYear === currentYear) {
        // Same year: show weekday and date (e.g., "Monday, Feb 11")
        return new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
        }).format(date);
    } else {
        // Different year: include year (e.g., "Tuesday, Dec 4, 2018")
        return new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(date);
    }
};

/**
 * Converts a timestamp into a friendly, fully localized date string using native browser Intl APIs.
 *
 * This implementation uses:
 * - Intl.RelativeTimeFormat for "today", "yesterday", "tomorrow" (automatically localized)
 * - Intl.DateTimeFormat for all other dates (weekday, month, day, year)
 * - Optional dateformat patterns for custom formatting (backward compatibility with friendlyDateFormatter)
 *
 * Examples:
 * - English (en-US): "Today", "Yesterday", "Tomorrow", "Monday, Feb 11", "Tuesday, Dec 4, 2018"
 * - Spanish (es-ES): "Hoy", "Ayer", "Mañana", "lunes, 11 feb", "martes, 4 dic 2018"
 * - French (fr-FR): "Aujourd'hui", "Hier", "Demain", "lundi 11 févr.", "mardi 4 déc. 2018"
 *
 * @param timestamp - The date to format (as number or Date object)
 * @param relativeTimestamp - The reference date to compare against (defaults to now)
 * @param locale - Intl locale string for date formatting (e.g., 'en-US', 'es-ES'). Takes precedence if both locale and localePreference are provided.
 * @param simpleFormat - Optional dateformat pattern for dates within the current year (e.g., 'd mmm')
 * @param longFormat - Optional dateformat pattern for dates in other years (e.g., 'd mmm yyyy')
 */
export const friendlyDateFormatterIntl = ({
    timestamp = Date.now(),
    relativeTimestamp = Date.now(),
    locale,
    simpleFormat,
    longFormat,
}: {
    timestamp?: number | Date;
    relativeTimestamp?: number | Date;
    locale?: string;
    simpleFormat?: string;
    longFormat?: string;
} = {}) => {
    const testTime = isObject(timestamp) ? timestamp.getTime() : timestamp;
    const relativeTime = isObject(relativeTimestamp) ? relativeTimestamp.getTime() : relativeTimestamp;

    const intlLocale = mapLocalePreferenceToIntlLocale(locale);

    return testTime > relativeTime
        ? formatFutureDateIntl(testTime, relativeTime, intlLocale, simpleFormat, longFormat)
        : formatPastDateIntl(testTime, relativeTime, intlLocale, simpleFormat, longFormat);
};
